import type {
  ConfirmAnchor,
  ConfirmAndRunOptions,
  ConfirmModalOptions,
  NotifyOptions,
  TherameModalType,
  TherameModalTypeMap,
} from '@/registries/modals';
import { logger } from '@/lib/logger';
import { canInstallAndroidPwa, canInstallIosPwa } from '@helpers/getBrowserDetails';
import { useSidebarStore } from '@stores/sidebarStore';
import { LegalDocument } from '@therame/shared';
import {
  Modal,
  createModalComposable,
  type ModalAnswer,
  type ModalCloseEvent,
  type ModalOpenEvent,
  type PeekAnchor,
} from '@therame/ui';
import { defineStore } from 'pinia';
import { Observable, Subject } from 'rxjs';
import { computed, ref, type ComputedRef, type Ref } from 'vue';

type SafeSubject<T> = Omit<Subject<T>, 'currentObservers'>;

/** What the call that opened a modal of type `T` gets back from its close. */
export type TherameModalAnswer<T extends TherameModalType> = ModalAnswer<T, TherameModalTypeMap>;

type PeekResolve<T extends TherameModalType> = () =>
  | ModalOpenEvent<T, TherameModalTypeMap>
  | null
  | Promise<ModalOpenEvent<T, TherameModalTypeMap> | null>;

// Captured from the prototype so an element we've already augmented (see
// openPeek) still resolves to the genuine native implementation, never its own
// override.
const nativeGetBoundingClientRect = Element.prototype.getBoundingClientRect;

/**
 * How long after a press the recorded element still counts as what raised a
 * dialogue. Long enough to cover the request a caller awaits before asking
 * ("darf ich löschen?" after a permission check), short enough that a
 * confirmation raised by something other than the user — a socket message, a
 * timer — falls back to the centred dialogue rather than pointing at whatever
 * button was last touched.
 */
const PRESS_ANCHOR_MAX_AGE_MS = 1500;

/**
 * The element the user last pressed, so `confirm` can anchor itself to the
 * button that raised it without all ~50 call sites threading a MouseEvent down
 * through the composables and menu items that stand between the click and the
 * dialogue. Capture phase, and `pointerdown` rather than `click`, so it is
 * recorded even where the handler stops propagation or acts on press.
 *
 * The rect is snapshotted here because the element frequently does not survive
 * the open: a `⋮` menu item unmounts as its menu closes, and a detached node
 * measures all-zero, which would fling the dialogue to the top-left corner.
 */
let lastPress: { el: HTMLElement; rect: DOMRect; at: number } | null = null;

if (typeof document !== 'undefined') {
  document.addEventListener(
    'pointerdown',
    (event) => {
      const target = event.target as HTMLElement | null;
      const el =
        target?.closest<HTMLElement>('button,[role="button"],a,[data-peek-anchor]') ?? null;
      lastPress = el
        ? { el, rect: nativeGetBoundingClientRect.call(el), at: performance.now() }
        : null;
    },
    true,
  );
}

/**
 * The control the user just pressed, while it still counts as what raised a peek:
 * for an open whose caller holds no event (a day picked in a calendar emits only
 * the day).
 */
export const lastPressedControl = (): HTMLElement | null =>
  lastPress && performance.now() - lastPress.at <= PRESS_ANCHOR_MAX_AGE_MS ? lastPress.el : null;

/**
 * Space a peek needs beside its anchor: the default `sm` step of the modal width
 * scale (420px) plus its 8px `:distance`. Wider types exist, but every open from
 * a region-sized anchor (the day-view appointment block) takes the default, and
 * a type on a wider step only misjudges by a hair — it narrows to the pointer
 * slightly later than ideal, never wrongly.
 */
const PEEK_SIDE_SPACE_PX = 428;

/**
 * The anchor rect collapsed to a zero-width column at `x`, keeping its vertical
 * extent so the peek still lines up with what it hangs off. For anchors too
 * wide to sit beside — see `openPeek`.
 */
const narrowedToPointer = (rect: DOMRect, x: number): DOMRect =>
  new DOMRect(x, rect.top, 0, rect.height);

/**
 * Whether an anchor leaves no room on either side for a peek — which is what a
 * day-view appointment block does: it spans the whole calendar column, so on a
 * 1512px viewport it leaves ~350px to its left and ~30px to its right. Both
 * `right-start` and its flip overflow, and Floating UI's `bestFit` fallback
 * hangs the peek off the edge of the screen.
 *
 * A synthetic click (keyboard activation reports 0/0) carries no pointer
 * position to narrow to, so it keeps the element anchor and its off-centre
 * placement rather than flinging the peek to x=0.
 */
const hasNoRoomBeside = (rect: DOMRect, event: MouseEvent): boolean =>
  (event.clientX !== 0 || event.clientY !== 0) &&
  rect.left < PEEK_SIDE_SPACE_PX &&
  window.innerWidth - rect.right < PEEK_SIDE_SPACE_PX;

/**
 * An anchor that reads the live rect while the element is in the document and
 * freezes on its last known one once it is not — so a dialogue raised from a
 * menu item still opens where that item was, and one raised from a button that
 * stays put follows it.
 */
const pressAnchor = (el: HTMLElement, rect: DOMRect): PeekAnchor => {
  let lastRect = rect;
  return {
    getBoundingClientRect: () => {
      if (el.isConnected) lastRect = nativeGetBoundingClientRect.call(el);
      return lastRect;
    },
    contextElement: el,
  };
};

type ModalStore = {
  fileDeleteTrigger: Ref<SafeSubject<void>>;
  userConsentGiven: Ref<boolean>;
  legalDocumentType: Ref<LegalDocument | undefined>;
  showLegalDocumentModal: (document: LegalDocument) => void;
  isLegalDocumentModalShown: ComputedRef<boolean>;
  hideLegalDocumentModal: () => void;
  screenShareError: Ref<boolean>;
  screenShareErrorDismissed: Ref<boolean>;
  anyModalOpen: ComputedRef<boolean>;
  getModal: (
    modalType: TherameModalType,
  ) => Modal<TherameModalType, TherameModalTypeMap> | undefined;
  isModalOpen: (modalType: TherameModalType) => boolean;
  /**
   * Open a modal and get back the answer of ITS close — never a sibling's of the
   * same type:
   *
   * ```ts
   * const { action, modalResult } = await modalStore.openModal({ type: 'selectPatient', props });
   * ```
   *
   * Settles exactly once, also after the caller has unmounted, and never rejects.
   */
  openModal: <T extends TherameModalType>(
    event: ModalOpenEvent<T, TherameModalTypeMap>,
  ) => Promise<TherameModalAnswer<T>>;
  /**
   * Open a dedicated modal anchored to the control the user just pressed — the
   * same implicit anchor `confirm` uses, and the same toggle (a second press on
   * that control puts the dialogue away instead of opening a second one).
   *
   * ```ts
   * modalStore.openAnchored({ type: 'issuePatientInvoice', props: { invoiceId, patientId } });
   * ```
   *
   * Exists because most dialogues are raised somewhere no `MouseEvent` reaches:
   * a footer-button config object, a dropdown's `@on-select`. `openPeek` needs
   * the event; this reads the press the store recorded instead, so a call site
   * anchors by saying nothing.
   *
   * Pass `anchor` for the cases the recorded press gets wrong — see
   * {@link ConfirmModalOptions.anchor}. Notably a pick from a list field: the
   * recorded press is the option row, which vanishes with the list, so pass the
   * field itself.
   *
   * Falls back to the centred presentation whenever there is no usable anchor,
   * on mobile, and whenever a centred modal is already open.
   */
  openAnchored: <T extends TherameModalType>(
    event: ModalOpenEvent<T, TherameModalTypeMap>,
    anchor?: ConfirmAnchor,
  ) => Promise<TherameModalAnswer<T>>;
  closeModal: <T extends TherameModalType>(event: ModalCloseEvent<T, TherameModalTypeMap>) => void;
  /**
   * Ask the user to confirm, resolving `true` only if they did. Replaces the
   * one-off "wirklich löschen?" modal components: pass the labels instead of
   * shipping a .vue file plus its own modal type.
   *
   * ```ts
   * if (await modalStore.confirm({ header: 'Preisliste löschen' })) deletePriceList(id);
   * ```
   *
   * Awaiting is enough — no subscription to clean up, and the caller still
   * holds the id it asked about, so nothing has to be echoed back through the
   * close event. For a dialogue that carries extra input (a reason picker, a
   * confirmation phrase), keep a dedicated modal component.
   *
   * Use this when what follows is immediate — local state, a panel close, a
   * navigation. When it is a request the user has to wait for, reach for
   * `confirmAndRun` instead, or the dialogue will be gone before there is
   * anything to show the wait on.
   *
   * On desktop the dialogue opens anchored to the control the user just
   * pressed — see `anchor` on {@link ConfirmModalOptions} for when to pass
   * something else, and when to opt out with `null`.
   */
  confirm: (options: ConfirmModalOptions) => Promise<boolean>;
  /**
   * Ask, then run the action with the dialogue still up so its confirm button
   * can spin. Resolves `true` only if the action actually ran and did not throw.
   *
   * ```ts
   * const deleted = await modalStore.confirmAndRun({
   *   header: 'Datei löschen?',
   *   action: () => deleteMutation.mutateAsync(id),
   * });
   * ```
   *
   * Split from `confirm` rather than added to it as a flag because the answers
   * mean different things — `confirm` resolves "the user said yes", this one
   * resolves "it happened". One signature that changed meaning depending on
   * which optional key was passed would hide that.
   */
  confirmAndRun: (options: ConfirmAndRunOptions) => Promise<boolean>;
  /** See `ModalComposable['holdAnswer']`: the confirmation running its action. */
  holdAnswer: (modal: Modal<TherameModalType, TherameModalTypeMap>) => () => void;
  /**
   * Tell the user something and wait for them to take it in. Replaces the
   * one-off "Information / Verstanden" modal components: pass the text instead
   * of shipping a .vue file plus its own modal type.
   *
   * ```ts
   * await modalStore.notify({
   *   header: 'Verbindung verloren',
   *   text: 'Die Verbindung zur Sprechstunde wurde unterbrochen.',
   * });
   * ```
   *
   * A third entry point rather than a flag on `confirm`, because it says
   * something different at the call site: `confirm` poses a question, this one
   * delivers a message. Hence `Promise<void>` — there is no answer to report,
   * only that it was seen, so the ✕ and Escape resolve it exactly as the button
   * does.
   *
   * Beware awaiting this inside a function whose own promise something else is
   * waiting on — the wait now includes a human. Where the point of awaiting is
   * to sequence a follow-up, reach for `notifyAndRun` instead.
   */
  notify: (options: NotifyOptions) => Promise<void>;
  /**
   * Tell the user something, then run the follow-up once the dialogue is gone.
   *
   * ```ts
   * void modalStore.notifyAndRun({
   *   header: 'Die Einheit steht noch nicht zur Verfügung.',
   *   action: () => router.push(coursePath),
   * });
   * ```
   *
   * Unlike `confirmAndRun`, the action runs *after* the close, not during it —
   * a notice has no outcome to wait on, so there is nothing to spin a button
   * for. Running it after is also the point: the dialogue closes itself with
   * `history.back()` (see `useHistoryIntercept`), so a navigation that fires
   * before the close lands on the entry from *before* the dialogue opened and
   * puts the user back where they just left.
   *
   * Callers should not await this — that would put a human back into their own
   * promise, which is the thing this exists to avoid. It returns a promise only
   * so a test can settle on it.
   */
  notifyAndRun: (
    options: NotifyOptions & { action: () => unknown | Promise<unknown> },
  ) => Promise<void>;
  /**
   * Open a modal as a popover anchored to the clicked element
   * (`event.currentTarget`). Re-clicking an element that already has a peek open
   * closes it (toggle) and skips `resolve` — so async work in `resolve` is
   * avoided on the close path. `resolve` returning null aborts the open.
   *
   * Resolves with the answer of the peek it opened, like `openModal`. A toggle
   * and an aborted open opened nothing, so they answer `cancel` at once.
   */
  openPeek: <T extends TherameModalType>(
    event: MouseEvent,
    resolve: PeekResolve<T>,
  ) => Promise<TherameModalAnswer<T>>;
  /**
   * `openPeek` anchored to a given element rather than to the one clicked — for a
   * peek raised from a menu entry that belongs to the button the menu hangs off.
   * Toggles on that element the same way. Without an element the peek is centred.
   */
  openPeekOn: <T extends TherameModalType>(
    el: HTMLElement | null,
    resolve: PeekResolve<T>,
  ) => Promise<TherameModalAnswer<T>>;
  /**
   * Every close of `type`, whoever opened the modal. For reacting to the close
   * itself — clearing a selection behind a peek. Never for the modal's answer: a
   * sibling's close arrives here too, so the answer is what `openModal` /
   * `openPeek` resolve with.
   */
  onModalClose: <T extends TherameModalType>(
    modalType: T,
  ) => Observable<ModalCloseEvent<T, TherameModalTypeMap>>;
  modalStack: Ref<Modal<TherameModalType, TherameModalTypeMap>[]>;
  closeAllModals: () => void;
  showPwaInstallPrompt: Ref<boolean>;
  closeInstallPrompt: () => void;
  openInstallPrompt: () => void;
  showIosInstallPrompt: Ref<boolean>;
};

export const useModalStore = defineStore('modal', (): ModalStore => {
  const _sidebarStore = useSidebarStore();
  const {
    modalStack,
    anyModalOpen,
    isModalOpen,
    getModal,
    openModal,
    closeModal,
    holdAnswer,
    closeAllModals,
    onModalClose,
  } = createModalComposable<TherameModalTypeMap>();

  const nothingOpened = <T extends TherameModalType>(): Promise<TherameModalAnswer<T>> =>
    Promise.resolve({ action: 'cancel' });

  /**
   * Pressing the control an anchored modal hangs off closes it again, rather
   * than opening a second one beside it — the anchor is the modal's handle, so
   * the press that opened it is also the press that puts it away. True when it
   * dismissed something, in which case the caller opens nothing.
   *
   * Shared by every anchored open: a peek (an appointment card) and an anchored
   * confirmation (the delete button that raised it) are the same gesture, and
   * one of them toggling but not the other is exactly the inconsistency the
   * user feels.
   */
  const dismissAnchoredOn = (el: HTMLElement | null): boolean => {
    if (!el) return false;
    // Identity by originating element, whether the anchor is raw or virtual.
    const sourceOf = (anchor: HTMLElement | PeekAnchor | null) =>
      anchor instanceof HTMLElement ? anchor : (anchor?.contextElement ?? null);

    const existing = modalStack.value.find(
      (modal) => !modal.closing && sourceOf(modal.anchorEl) === el,
    );
    if (!existing) return false;

    closeModal({ type: existing.modalType, action: 'cancel' });
    return true;
  };

  const openPeek: ModalStore['openPeek'] = (event, resolve) =>
    // Read synchronously: event.currentTarget is only valid during the click's
    // dispatch, and resolve may await.
    openAnchoredPeek((event.currentTarget as HTMLElement | null) ?? null, event, resolve);

  // No pointer to narrow to, so the element stays the anchor (see `hasNoRoomBeside`).
  const openPeekOn: ModalStore['openPeekOn'] = (el, resolve) => openAnchoredPeek(el, null, resolve);

  const openAnchoredPeek = async <T extends TherameModalType>(
    el: HTMLElement | null,
    event: MouseEvent | null,
    resolve: PeekResolve<T>,
  ): Promise<TherameModalAnswer<T>> => {
    // Snapshot the rect now — it seeds the detach-resilient anchor below, so even
    // an element that detaches during resolve() still has a valid last-known
    // position.
    const rect = el ? nativeGetBoundingClientRect.call(el) : null;
    // Decided here, at open, for the same reason. Re-testing on every rect read
    // would let a resize swap the anchor out from under a peek the user is
    // reading, and the point they clicked stops existing the moment the peek
    // covers it.
    const toPointer = rect && event ? hasNoRoomBeside(rect, event) : false;
    const pointerX = event?.clientX ?? 0;

    if (dismissAnchoredOn(el)) return nothingOpened();

    const modalEvent = await resolve();
    if (!modalEvent) return nothingOpened();

    // The clicked element stays floating-vue's positioning reference: it must be
    // a real DOM node, because floating-vue walks the node's actual scroll
    // ancestors (getOverflowAncestors) to know when to reposition — that's what
    // lets the peek follow the card as the calendar scrolls. A virtual element
    // (rect snapshot) has no scroll ancestors, so the peek would stop tracking.
    //
    // But the card can detach AFTER the peek opens: materializing a series
    // occurrence invalidates the calendar query, and the refetch re-renders the
    // card a few ticks later. A detached node's getBoundingClientRect reads
    // all-zero, which snaps the peek to the top-left on the next reposition. A
    // point-in-time `el.isConnected` check here can't catch that later detach
    // (it races the refetch). So augment the node's getBoundingClientRect to
    // freeze on the last good rect once it detaches — behaviour-preserving while
    // connected (returns the live native rect, exactly as before, so scroll
    // tracking is unchanged), resilient once it isn't.
    //
    // The same augmentation carries the wide-anchor case. When nothing fits
    // beside the element there is also nothing to sit beside — a day-view block
    // IS the column — so the click point becomes the reference and the peek
    // lands over the block, which is the only place it can go. Narrowing the
    // real node's rect rather than swapping in a virtual `PeekAnchor` is what
    // keeps the scroll tracking described above: the reference stays the node
    // whose ancestors floating-vue walks.
    if (el && rect) {
      let lastRect = rect;
      Object.defineProperty(el, 'getBoundingClientRect', {
        configurable: true,
        value: function (this: HTMLElement): DOMRect {
          if (this.isConnected) lastRect = nativeGetBoundingClientRect.call(this);
          return toPointer ? narrowedToPointer(lastRect, pointerX) : lastRect;
        },
      });
    }

    return openModal({ ...modalEvent, anchorEl: el });
  };

  /**
   * What a confirmation would anchor to, if anything: the anchor itself, plus
   * the control it hangs off — kept apart because the control is also the
   * toggle target, and a caller-measured anchor may have no element at all.
   *
   * `undefined` (the usual case, nothing passed) takes the element the user
   * just pressed; `null` opts out and keeps the centred dialogue; anything else
   * is the call site being explicit — an event it still holds, or an element it
   * owns.
   */
  const anchorFor = (
    anchor: ConfirmAnchor | undefined,
  ): { anchorEl: HTMLElement | PeekAnchor; el: HTMLElement | null } | null => {
    if (anchor === null) return null;

    if (anchor === undefined) {
      if (!lastPress || performance.now() - lastPress.at > PRESS_ANCHOR_MAX_AGE_MS) return null;
      // The press rect, not a fresh measurement: by now the control may be gone.
      return { anchorEl: pressAnchor(lastPress.el, lastPress.rect), el: lastPress.el };
    }

    if (anchor instanceof MouseEvent) {
      // `currentTarget` is only live during dispatch, and callers routinely ask
      // after awaiting something — so fall back to the pressed control the
      // event started on.
      const el =
        (anchor.currentTarget as HTMLElement | null) ??
        (anchor.target as HTMLElement | null)?.closest<HTMLElement>('button,[role="button"],a') ??
        null;
      return el ? { anchorEl: pressAnchor(el, nativeGetBoundingClientRect.call(el)), el } : null;
    }

    if (anchor instanceof HTMLElement) {
      return {
        anchorEl: pressAnchor(anchor, nativeGetBoundingClientRect.call(anchor)),
        el: anchor,
      };
    }

    // Caller-measured (a table cell, a canvas region): taken as given.
    return { anchorEl: anchor, el: anchor.contextElement ?? null };
  };

  /**
   * Drops the anchor whenever a centred modal is already open: the peek popper
   * deliberately sits below the modal layer (see the z-index note in
   * `GenericModal.vue`), so a confirmation anchored on top of one would
   * disappear behind that modal's scrim. Centred is the only correct answer
   * there.
   *
   * Anchored modals in the stack don't count — they carry no scrim and sit in
   * the same layer, so a confirmation raised from another control while one is
   * still on screen (or lingering through its 200ms close) stays anchored to
   * the control it belongs to.
   */
  const anchoredPresentation = (target: ReturnType<typeof anchorFor>) =>
    target && !modalStack.value.some((modal) => !modal.closing && !modal.anchorEl)
      ? target.anchorEl
      : null;

  /**
   * Everything an about-to-open dialogue needs to know about its anchor: what
   * to hang off, or that the press dismissed one already hanging off the same
   * control and there is nothing left to open.
   *
   * One resolver rather than a copy per entry point, so the toggle and the
   * centred-modal fallback cannot drift between `confirm`, `confirmAndRun` and
   * `openAnchored` — they are the same gesture and must stay the same rules.
   */
  const resolveAnchor = (
    anchor: ConfirmAnchor | undefined,
  ): { dismissed: true } | { dismissed: false; anchorEl: HTMLElement | PeekAnchor | null } => {
    const target = anchorFor(anchor);
    if (target && dismissAnchoredOn(target.el)) return { dismissed: true };
    return { dismissed: false, anchorEl: anchoredPresentation(target) };
  };

  const openAnchored: ModalStore['openAnchored'] = (event, anchor) => {
    const resolved = resolveAnchor(anchor);
    if (resolved.dismissed) return nothingOpened();
    return openModal({ ...event, anchorEl: resolved.anchorEl });
  };

  const confirm: ModalStore['confirm'] = ({ anchor, ...options }) => {
    const resolved = resolveAnchor(anchor);
    // A second press on the same control puts the question away instead of
    // asking it again — nothing was confirmed, hence `false`.
    if (resolved.dismissed) return Promise.resolve(false);

    return openModal({ type: 'confirm', props: options, anchorEl: resolved.anchorEl }).then(
      (answer) => answer.action === 'confirm',
    );
  };

  // `ok` is what the dialogue reports about the action; a cancel never carries
  // one, so an untagged dismissal before the action started reads as false here
  // too.
  const confirmAndRun: ModalStore['confirmAndRun'] = ({ anchor, ...options }) => {
    const resolved = resolveAnchor(anchor);
    // Same toggle, and `false` is as true here as it is above: the action did
    // not run.
    if (resolved.dismissed) return Promise.resolve(false);

    return openModal({ type: 'confirm', props: options, anchorEl: resolved.anchorEl }).then(
      (answer) => answer.action === 'confirm' && answer.modalResult?.ok === true,
    );
  };

  // Every close means the same thing here — the user has seen it — so there is
  // nothing to read out of the event.
  const notify: ModalStore['notify'] = (options) =>
    openModal({ type: 'notify', props: options }).then(() => undefined);

  const notifyAndRun: ModalStore['notifyAndRun'] = async ({ action, ...options }) => {
    await notify(options);
    try {
      await action();
    } catch (error) {
      // Swallowed for the same reason `confirmAndRun` swallows: the caller
      // reports failures its own way. Logged so a caller that forgot to report
      // isn't silent.
      logger.error({ msg: 'Acknowledged action failed', error });
    }
  };

  const fileDeleteTrigger = ref<Subject<void>>(new Subject<void>());

  const screenShareError = ref<boolean>(false);
  const screenShareErrorDismissed = ref<boolean>(false);

  const showPwaInstallPrompt = ref<boolean>(false);
  const showIosInstallPrompt = ref<boolean>(false);

  const legalDocumentType = ref<LegalDocument>();
  const showLegalDocumentModal = (document: LegalDocument) => {
    legalDocumentType.value = document;
    openModal({ type: 'legalDocument', props: { legalDocument: document } });
  };

  const isLegalDocumentModalShown = computed(() => {
    return (
      modalStack.value.filter((stackItem) => stackItem.modalType === 'legalDocument').length > 0
    );
  });

  const hideLegalDocumentModal = () => {
    closeModal({
      type: 'legalDocument',
      action: 'cancel',
      modalResult: { legalDocument: legalDocumentType.value },
    });
    legalDocumentType.value = undefined;
  };

  const userConsentGiven = ref<boolean>(false);

  const openInstallPrompt = () => {
    if (canInstallIosPwa) {
      showIosInstallPrompt.value = true;
    } else if (canInstallAndroidPwa) {
      showPwaInstallPrompt.value = true;
    }
  };

  const closeInstallPrompt = () => {
    showPwaInstallPrompt.value = false;
  };

  const store: ModalStore = {
    fileDeleteTrigger,
    userConsentGiven,
    legalDocumentType,
    showLegalDocumentModal,
    isLegalDocumentModalShown,
    hideLegalDocumentModal,
    screenShareError,
    screenShareErrorDismissed,
    anyModalOpen,
    getModal,
    isModalOpen,
    openModal,
    openAnchored,
    closeModal,
    confirm,
    confirmAndRun,
    holdAnswer,
    notify,
    notifyAndRun,
    openPeek,
    openPeekOn,
    onModalClose,
    modalStack,
    closeAllModals,
    showPwaInstallPrompt,
    closeInstallPrompt,
    openInstallPrompt,
    showIosInstallPrompt,
  };
  return store;
});
